/** Ejecuta acciones de workflow con revisión, idempotencia e invalidación. */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { tenantIdOrNull, tenantScoped } from '@/config/queryClient';
import { commercialService } from '@/services/commercialService';
import { extractErrorMessage, extractRevisionConflict } from '@/services/mobileApiClient';
import {
  tasksService,
  type WorkflowContextAction,
  type WorkflowExecutionKind,
} from '@/services/tasksService';
import { useMobileAuth } from '@/state/MobileAuthContext';
import {
  dispatchWorkflowAction,
  workflowActionCode,
  type WorkflowActionHandlers,
  type WorkflowActionPayload,
} from '@/utils/workflowActions';

export type DomainCommandExecutor = (
  actionCode: string,
  payload: WorkflowActionPayload,
  revision?: number,
) => Promise<unknown>;

export type UseMobileWorkflowActionsOptions = {
  entityCode: string;
  entityId: string;
  actions: WorkflowContextAction[];
  documentRevision?: number;
  taskRevision?: number;
  instanceRevision?: number;
  activeInstanceId?: string | null;
  onDomainCommand?: DomainCommandExecutor;
  onSuccess?: (
    actionCode: string,
    result: unknown,
    action: WorkflowContextAction,
  ) => void;
  onConflict?: () => void;
};

function requireReason(payload: WorkflowActionPayload, label: string): string {
  const reason = typeof payload.reason === 'string' ? payload.reason.trim() : '';
  if (!reason) throw new Error(`El motivo es obligatorio para ${label}`);
  return reason;
}

function decisionFrom(code: string): 'approve' | 'reject' {
  if (code === 'approve' || code === 'reject') return code;
  throw new Error(`Decisión no soportada: "${code}"`);
}

export function useMobileWorkflowActions({
  entityCode,
  entityId,
  actions,
  documentRevision,
  taskRevision,
  instanceRevision,
  activeInstanceId,
  onDomainCommand,
  onSuccess,
  onConflict,
}: UseMobileWorkflowActionsOptions) {
  const queryClient = useQueryClient();
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function defaultDomainCommand(
    actionCode: string,
    payload: WorkflowActionPayload,
    revision?: number,
  ): Promise<unknown> {
    if (entityCode === 'customer_requirement') {
      return commercialService.requirementAction(entityId, actionCode, payload, revision);
    }
    if (entityCode === 'budget') {
      return commercialService.budgetAction(entityId, actionCode, payload, revision);
    }
    if (entityCode === 'quotation') {
      return commercialService.quotationAction(entityId, actionCode, payload, revision);
    }
    throw new Error(`Entidad sin comando de dominio: "${entityCode}"`);
  }

  async function execute(
    actionCode: string,
    payload: WorkflowActionPayload = {},
  ): Promise<unknown> {
    const action = actions.find((entry) => workflowActionCode(entry) === actionCode);
    if (!action) throw new Error(`Acción no disponible: "${actionCode}"`);

    const domainExecutor = onDomainCommand ?? defaultDomainCommand;
    const domainCommands: Record<string, WorkflowActionHandlers['request']> = {};
    for (const available of actions) {
      if (available.execution_kind !== 'domain_command') continue;
      domainCommands[workflowActionCode(available)] = async (current, body) =>
        domainExecutor(workflowActionCode(current), body, documentRevision);
    }

    const handlers: WorkflowActionHandlers = {
      domainCommands,
      request: async (current) => {
        if (!current.workflow_point_code || documentRevision === undefined) {
          throw new Error('Falta el punto de workflow o la revisión del documento');
        }
        return tasksService.requestWorkflow({
          entityCode,
          entityId,
          workflowPointCode: current.workflow_point_code,
          expectedDocumentRevision: documentRevision,
        });
      },
      decision: async (current, body) => {
        if (!current.task_id || taskRevision === undefined) {
          throw new Error('Falta la tarea o su revisión para decidir');
        }
        return tasksService.decide(current.task_id, {
          decision: decisionFrom(workflowActionCode(current)),
          reason: typeof body.reason === 'string' ? body.reason.trim() || undefined : undefined,
          expectedTaskRevision: taskRevision,
        });
      },
      withdraw: async (_current, body) => {
        const reason = requireReason(body, 'retirar la solicitud');
        if (!activeInstanceId || instanceRevision === undefined) {
          throw new Error('Falta la solicitud activa o su revisión para retirar');
        }
        return tasksService.withdraw(activeInstanceId, {
          reason,
          expectedInstanceRevision: instanceRevision,
        });
      },
      resume: async (_current, body) => {
        const reason = requireReason(body, 'reanudar la solicitud');
        if (!activeInstanceId || instanceRevision === undefined) {
          throw new Error('Falta la solicitud activa o su revisión para reanudar');
        }
        return tasksService.resume(activeInstanceId, {
          reason,
          expectedInstanceRevision: instanceRevision,
        });
      },
      reassign: async (current, body) => {
        const reason = requireReason(body, 'reasignar la tarea');
        if (!current.task_id || taskRevision === undefined) {
          throw new Error('Falta la tarea o su revisión para reasignar');
        }
        if (typeof body.membership_id !== 'string' || body.membership_id.trim() === '') {
          throw new Error('Falta la membresía destino');
        }
        return tasksService.reassign(current.task_id, {
          membershipId: body.membership_id,
          reason,
          expectedTaskRevision: taskRevision,
        });
      },
    };

    setBusyAction(actionCode);
    setError(null);
    try {
      const result = await dispatchWorkflowAction(action, payload, handlers);
      if (tenantId !== null) {
        void queryClient.invalidateQueries({ queryKey: tenantScoped(tenantId) });
      }
      onSuccess?.(actionCode, result, action);
      return result;
    } catch (cause) {
      if (extractRevisionConflict(cause)) onConflict?.();
      setError(extractErrorMessage(cause));
      throw cause;
    } finally {
      setBusyAction(null);
    }
  }

  return {
    execute,
    busyAction,
    error,
    clearError: () => setError(null),
    executionKinds: actions.reduce<Record<string, WorkflowExecutionKind | undefined>>(
      (accumulator, action) => {
        accumulator[workflowActionCode(action)] = action.execution_kind;
        return accumulator;
      },
      {},
    ),
  };
}
